import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Loader2, Check, CalendarDays } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { weekdayNames, monthNames } from "@/data/content";
import PageHeader from "@/components/site/PageHeader";
import { Reveal } from "@/components/site/Reveal";
import { getAvailability, createBooking } from "@/lib/api";

function formatLongDate(dateStr, weekday) {
  const [y, m, d] = dateStr.split("-").map(Number);
  return `${weekdayNames[weekday]} ${d} de ${monthNames[m - 1]}`;
}

const emptyForm = { name: "", email: "", phone: "", modality: "", message: "" };

export default function Agenda() {
  const [days, setDays] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const data = await getAvailability();
      setDays(data.days || []);
      if (data.days && data.days.length) setSelectedDate(data.days[0].date);
    } catch (e) {
      toast.error("No se pudieron cargar los horarios.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const currentDay = useMemo(
    () => days.find((d) => d.date === selectedDate),
    [days, selectedDate]
  );

  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!selectedSlot) {
      toast.error("Selecciona un horario.");
      return;
    }
    if (!form.name || !form.email || !form.phone || !form.modality) {
      toast.error("Completa nombre, correo, teléfono y modalidad.");
      return;
    }
    setSubmitting(true);
    try {
      const booking = await createBooking({ ...form, slot: selectedSlot.iso });
      setDone(booking);
      toast.success("Primera entrevista agendada.");
      setForm(emptyForm);
      setSelectedSlot(null);
      load();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(detail || "No se pudo agendar. Inténtalo nuevamente.");
      if (err?.response?.status === 409) load();
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    const [datePart, timePart] = done.slot.split("T");
    const [y, m, d] = datePart.split("-").map(Number);
    return (
      <div data-testid="agenda-page" className="min-h-[70vh]">
        <PageHeader overline="Agenda" titleLines={["Reserva", "confirmada"]} />
        <section className="max-w-[900px] mx-auto px-5 md:px-10 pb-32">
          <Reveal>
            <div className="border border-[#9C5A4B]/30 bg-[#efece5] p-10 md:p-14">
              <div className="w-14 h-14 rounded-full bg-[#9C5A4B] text-white flex items-center justify-center mb-8">
                <Check size={26} />
              </div>
              <p className="font-display text-3xl md:text-4xl leading-tight mb-4">
                Gracias, {done.name}.
              </p>
              <p className="text-lg text-[#1a1a1a]/80 leading-relaxed max-w-xl">
                Tu primera entrevista quedó agendada para el{" "}
                <strong>
                  {d} de {monthNames[m - 1]} de {y} a las {timePart.slice(0, 5)}
                </strong>{" "}
                horas (hora de Santiago), en modalidad{" "}
                <strong>{done.modality}</strong>. Recibirás una confirmación por
                correo. Si necesitas cambiar la hora, escríbeme.
              </p>
              <button
                onClick={() => setDone(null)}
                data-testid="agenda-new-booking"
                className="mt-8 inline-flex items-center gap-2 text-sm border-b border-[#1a1a1a] pb-1 hover:text-[#9C5A4B] hover:border-[#9C5A4B] transition-colors duration-300"
              >
                Agendar otra hora
              </button>
            </div>
          </Reveal>
        </section>
      </div>
    );
  }

  return (
    <div data-testid="agenda-page">
      <PageHeader
        overline="Agenda"
        titleLines={["Reserva tu", "primera entrevista"]}
        intro="Elige un día y un horario disponible en los próximos 30 días. La sesión tiene una duración aproximada de 50 minutos."
      />

      <section className="max-w-[1400px] mx-auto px-5 md:px-10 pb-24 md:pb-32">
        {loading ? (
          <div className="flex items-center gap-3 text-[#5c5c5c] py-20" data-testid="agenda-loading">
            <Loader2 className="animate-spin" size={20} /> Cargando horarios disponibles…
          </div>
        ) : days.length === 0 ? (
          <div className="py-20 text-center" data-testid="agenda-empty">
            <CalendarDays size={32} className="mx-auto text-[#9C5A4B] mb-4" />
            <p className="text-lg text-[#5c5c5c]">
              No hay horarios disponibles por ahora. Escríbeme y coordinamos directamente.
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14">
            {/* Date + slot picker */}
            <div className="lg:col-span-7">
              <p className="overline mb-5">1 · Elige un día</p>
              <div className="flex gap-3 overflow-x-auto pb-3 -mx-1 px-1" data-testid="agenda-dates">
                {days.map((day) => {
                  const active = day.date === selectedDate;
                  const [, m, d] = day.date.split("-").map(Number);
                  return (
                    <button
                      key={day.date}
                      onClick={() => {
                        setSelectedDate(day.date);
                        setSelectedSlot(null);
                      }}
                      data-testid={`date-${day.date}`}
                      className={`shrink-0 w-24 py-4 border text-center transition-colors duration-300 ${
                        active
                          ? "bg-[#2A2C29] text-[#f9f8f6] border-[#2A2C29]"
                          : "bg-transparent border-black/15 hover:border-[#9C5A4B]"
                      }`}
                    >
                      <span className="block text-xs uppercase tracking-wider opacity-70">
                        {weekdayNames[day.weekday].slice(0, 3)}
                      </span>
                      <span className="block font-display text-2xl mt-1">{d}</span>
                      <span className="block text-xs opacity-70">{monthNames[m - 1].slice(0, 3)}</span>
                    </button>
                  );
                })}
              </div>

              <p className="overline mt-10 mb-5">2 · Elige un horario</p>
              {currentDay && (
                <p className="text-[#5c5c5c] mb-4 capitalize">
                  {formatLongDate(currentDay.date, currentDay.weekday)}
                </p>
              )}
              <div className="grid grid-cols-3 sm:grid-cols-4 gap-3" data-testid="agenda-slots">
                {currentDay?.slots.map((slot) => {
                  const active = selectedSlot?.iso === slot.iso;
                  return (
                    <button
                      key={slot.iso}
                      onClick={() => setSelectedSlot(slot)}
                      data-testid={`slot-${slot.iso}`}
                      className={`py-3 border text-sm transition-colors duration-300 ${
                        active
                          ? "bg-[#9C5A4B] text-[#f9f8f6] border-[#9C5A4B]"
                          : "border-black/15 hover:border-[#9C5A4B]"
                      }`}
                    >
                      {slot.time}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Booking form */}
            <div className="lg:col-span-5">
              <div className="bg-[#efece5] p-7 md:p-9 lg:sticky lg:top-28">
                <p className="overline mb-6">3 · Tus datos</p>
                <form onSubmit={submit} className="space-y-5" data-testid="booking-form">
                  <div>
                    <Label htmlFor="b-name" className="overline">Nombre</Label>
                    <Input
                      id="b-name"
                      data-testid="booking-name"
                      value={form.name}
                      onChange={update("name")}
                      className="mt-2 rounded-none border-black/20 bg-[#f9f8f6] h-12 focus-visible:ring-0 focus-visible:border-[#9C5A4B]"
                      placeholder="Tu nombre"
                    />
                  </div>
                  <div>
                    <Label htmlFor="b-email" className="overline">Correo</Label>
                    <Input
                      id="b-email"
                      type="email"
                      data-testid="booking-email"
                      value={form.email}
                      onChange={update("email")}
                      className="mt-2 rounded-none border-black/20 bg-[#f9f8f6] h-12 focus-visible:ring-0 focus-visible:border-[#9C5A4B]"
                      placeholder="tucorreo@ejemplo.com"
                    />
                  </div>
                  <div>
                    <Label htmlFor="b-phone" className="overline">Teléfono</Label>
                    <Input
                      id="b-phone"
                      data-testid="booking-phone"
                      value={form.phone}
                      onChange={update("phone")}
                      className="mt-2 rounded-none border-black/20 bg-[#f9f8f6] h-12 focus-visible:ring-0 focus-visible:border-[#9C5A4B]"
                      placeholder="+56 9 ..."
                    />
                  </div>
                  <div>
                    <Label className="overline">Modalidad</Label>
                    <Select
                      value={form.modality}
                      onValueChange={(v) => setForm({ ...form, modality: v })}
                    >
                      <SelectTrigger
                        data-testid="booking-modality"
                        className="mt-2 rounded-none border-black/20 bg-[#f9f8f6] h-12 focus:ring-0"
                      >
                        <SelectValue placeholder="Selecciona una modalidad" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Presencial">Presencial</SelectItem>
                        <SelectItem value="Online">Online</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label htmlFor="b-message" className="overline">
                      Mensaje (opcional)
                    </Label>
                    <Textarea
                      id="b-message"
                      data-testid="booking-message"
                      value={form.message}
                      onChange={update("message")}
                      rows={3}
                      className="mt-2 rounded-none border-black/20 bg-[#f9f8f6] focus-visible:ring-0 focus-visible:border-[#9C5A4B] resize-none"
                      placeholder="Algo que quieras contarme antes."
                    />
                  </div>

                  <div className="pt-1 text-sm text-[#5c5c5c] min-h-[20px]" data-testid="booking-selected">
                    {selectedSlot ? (
                      <span>
                        Horario:{" "}
                        <strong className="text-[#1a1a1a]">
                          {formatLongDate(currentDay.date, currentDay.weekday)} · {selectedSlot.time} h
                        </strong>
                      </span>
                    ) : (
                      <span>Selecciona un horario más arriba.</span>
                    )}
                  </div>

                  <button
                    type="submit"
                    disabled={submitting}
                    data-testid="booking-submit"
                    className="w-full inline-flex items-center justify-center gap-3 rounded-full bg-[#9C5A4B] text-[#f9f8f6] px-8 py-4 text-sm tracking-wide hover:bg-[#8A4A3C] transition-colors duration-300 disabled:opacity-60"
                  >
                    {submitting && <Loader2 size={18} className="animate-spin" />}
                    Confirmar primera entrevista
                  </button>
                </form>
              </div>
            </div>
          </div>
        )}
      </section>
    </div>
  );
}
